import type { JobFinancialSummaryDto } from '../types';

interface JobFinancialSummaryProps {
  financial: JobFinancialSummaryDto;
}

export function JobFinancialSummary({ financial }: JobFinancialSummaryProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercent = (percent: number) => {
    return `${percent.toFixed(2)}%`;
  };

  return (
    <div className="bg-card p-1.5 rounded-lg border border-border">
      <h3 className="text-secondary font-semibold text-sm mb-1">Financial Summary</h3>

      <div className="grid grid-cols-3 gap-3">
        {/* Column 1 */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Total Charges:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.totalCharges)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Total Costs:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.totalCosts)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Total Tax:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.totalTax)}</span>
          </div>
        </div>

        {/* Column 2 */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Payments Received:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.totalPaymentsReceived)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Payments Pending:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.totalPaymentsPending)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Customer Balance:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.customerBalance)}</span>
          </div>
        </div>

        {/* Column 3 */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Profit:</span>
            <span className={`text-sm font-semibold ${financial.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(financial.profit)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Profit %:</span>
            <span className={`text-sm font-semibold ${financial.profitPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatPercent(financial.profitPercent)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Job Balance:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.jobBalance)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
